import React, { useEffect } from "react";
import { Link } from "react-scroll";
import { useTranslation } from "react-i18next";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import useTheme from "../hooks/useTheme";
import useLanguage from "../hooks/useLanguage";
import { signOutLocal } from "../config/localAuth";
import BrandLogo from "./BrandLogo";

function ThemeIcon({ theme }) {
  if (theme === "dark") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="4.25" stroke="currentColor" strokeWidth="1.75" />
        <path
          d="M12 2.75v1.5M12 19.75v1.5M21.25 12h-1.5M4.25 12h-1.5M18.54 5.46l-1.06 1.06M6.52 17.48l-1.06 1.06M18.54 18.54l-1.06-1.06M6.52 6.52 5.46 5.46"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20.2 14.35A7.75 7.75 0 0 1 9.65 3.8 7.9 7.9 0 1 0 20.2 14.35Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userEmail, userName, auth } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, toggleLanguage } = useLanguage();
  const userLabel = userName || userEmail;
  const langLabel = language === "am" ? "EN" : "አማ";
  const langAria =
    language === "am" ? t("navbar.langToEnglish") : t("navbar.langToAmharic");

  const handleLogout = async () => {
    try {
      signOutLocal();
      if (auth) {
        await signOut(auth);
      }
    } finally {
      navigate("/");
    }
  };

  useEffect(() => {
    const onScroll = () => {
      const gotop = document.querySelector(".gotop");
      const nav = document.querySelector(".navbar");
      if (!gotop || !nav) return;

      const threshold = window.innerWidth < 768 ? 420 : 160;
      if (window.scrollY > threshold) {
        gotop.classList.add("display");
        nav.classList.add("navopened");
      } else {
        gotop.classList.remove("display");
        nav.classList.remove("navopened");
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  function openBar() {
    const bar = document.querySelector(".bar");
    if (bar) bar.classList.toggle("opened");
  }

  return (
    <nav className="navbar">
      <div className="container">
        <div className="row">
          <div className="logo">
            <BrandLogo />
          </div>
          <ul className="bar">
            <li>
              <Link onClick={openBar} activeClass="active" spy={true} smooth={true} duration={800} to="headerbg">
                {t("navbar.home")}
              </Link>
            </li>
            <li>
              <Link onClick={openBar} activeClass="active" to="services" spy={true} smooth={true} duration={800}>
                {t("navbar.services")}
              </Link>
            </li>
            <li>
              <Link onClick={openBar} to="about-scroll" spy={true} smooth={true} duration={800} activeClass="active">
                {t("navbar.about")}
              </Link>
            </li>
            <li>
              <Link onClick={openBar} to="contact" spy={true} smooth={true} duration={800} activeClass="active">
                {t("navbar.contact")}
              </Link>
            </li>
          </ul>
          <div className="nav-toolbar">
            <button
              type="button"
              className="lang-toggle"
              onClick={toggleLanguage}
              aria-label={langAria}
              title={langAria}
            >
              <span className="lang-toggle-label">{langLabel}</span>
            </button>
            <button
              type="button"
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label={theme === "dark" ? t("navbar.themeToLight") : t("navbar.themeToDark")}
              title={theme === "dark" ? t("navbar.themeToLight") : t("navbar.themeToDark")}
            >
              <ThemeIcon theme={theme} />
            </button>
          </div>
          {userLabel && (
            <div className="nav-user">
              <div className="nav-user-chip" title={userEmail || userLabel}>
                <span className="nav-user-avatar" aria-hidden="true">
                  {userLabel.charAt(0).toUpperCase()}
                </span>
                <span className="nav-user-email">{userLabel}</span>
              </div>
              <button type="button" onClick={handleLogout} className="nav-logout">
                {t("navbar.logout")}
              </button>
            </div>
          )}
          <div className="button" onClick={openBar} role="button" tabIndex={0} aria-label={t("navbar.toggleMenu")}>
            <div className="burger"></div>
            <div className="burger"></div>
            <div className="burger"></div>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
